import { Card, Input, Select, Typography } from 'antd'
import RatingGuideTable from './RatingGuideTable'

const { Text } = Typography
const { TextArea } = Input
const { Option } = Select

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 16,
  lineHeight: 1.5
}

const headerStyle = {
  padding: '10px 12px',
  border: '1px solid #d9d9d9',
  background: 'var(--neutral-grey0)',
  textAlign: 'left',
  whiteSpace: 'pre-line',
  fontWeight: 700,
  lineHeight: 1.5
}

const cellStyle = {
  padding: '10px 12px',
  border: '1px solid #d9d9d9',
  verticalAlign: 'top',
  lineHeight: 1.5
}

export default function PerformanceFactorsCard({
  formStructure,
  formConfig,
  getFieldValue,
  updateField,
  getQuestionRatingOptions,
  getRatingOptionLabel,
  getEmployeeRatingOptionLabel = getRatingOptionLabel,
  getRatingValueDisplay,
  editableSide,
  visibleSide,
  managerSubmitted = false,
  showValidationErrors = false,
  embedRatingScaleTable = true
}) {
  const factorConfig = formConfig?.checkbox_flow?.performance_factors || {}
  const appraisalProcessConfig = formConfig?.appraisal_process || {}
  const questionKey = factorConfig.question_key || 'q1'
  const employeeEvaluationLabel = appraisalProcessConfig.employee_evaluation_label || 'Employee Self-Evaluation'
  const managerEvaluationLabel = appraisalProcessConfig.manager_evaluation_label || 'Manager Evaluation'
  const ratingInputLabel = appraisalProcessConfig.rating_input_label || 'Rating'
  const examplesLabel = appraisalProcessConfig.examples_label || 'Comments'
  const examplesRequired = appraisalProcessConfig.examples_required === true
  const optionalPerformanceFactorKeys = new Set(appraisalProcessConfig.optional_performance_factor_keys || [])
  // Examples can still be required for validation, but we no longer surface the
  // required asterisk in the column header / placeholder.
  const displayedExamplesLabel = examplesLabel
  const notRatedLabel = appraisalProcessConfig.not_rated_label || 'Not rated'
  const noExamplesLabel = appraisalProcessConfig.no_examples_label || 'No comments'
  const pendingManagerReviewLabel = appraisalProcessConfig.pending_manager_review_label || 'Pending manager review'
  const renderRating = value => getRatingValueDisplay(value) || <Text type='secondary'>{notRatedLabel}</Text>
  const title = factorConfig.title || appraisalProcessConfig.performance_factors_title || 'Performance Factors'
  const factorColumnLabel = appraisalProcessConfig.performance_factor_label || appraisalProcessConfig.performance_factors_title || 'Performance Factor'
  const description = factorConfig.description || appraisalProcessConfig.performance_factors_description ||
    'Evaluate each performance factor and give specific examples to support the rating.'
  const showRatingScaleTable = embedRatingScaleTable && appraisalProcessConfig.show_rating_scale_table === true

  const renderEditableRating = (category, question, value, side) => (
    <Select
      style={{ width: '100%', minWidth: 190 }}
      value={value}
      onChange={rating => updateField(category.id, question.id, `${side}_rating`, rating)}
      placeholder={ratingInputLabel}
      allowClear
      optionLabelProp='label'
      status={showValidationErrors && !value && !optionalPerformanceFactorKeys.has(category.id) ? 'error' : undefined}
    >
      {getQuestionRatingOptions(value).map(option => {
        const optionLabel = side === 'employee'
          ? getEmployeeRatingOptionLabel(option)
          : getRatingOptionLabel(option)
        return (
          <Option key={option.value} value={option.value} label={optionLabel}>
            {optionLabel}
          </Option>
        )
      })}
    </Select>
  )

  const renderTable = () => (
    <div style={{ overflowX: 'auto' }}>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th rowSpan={2} style={{ ...headerStyle, width: '20%' }}>{factorColumnLabel}</th>
            {visibleSide !== 'manager' && (
              <th colSpan={2} style={headerStyle}>{employeeEvaluationLabel}</th>
            )}
            {visibleSide !== 'employee' && (
              <th colSpan={2} style={headerStyle}>{managerEvaluationLabel}</th>
            )}
          </tr>
          <tr>
            {visibleSide !== 'manager' && (
              <>
                <th style={{ ...headerStyle, width: '22%', minWidth: 210 }}>{ratingInputLabel}</th>
                <th style={headerStyle}>{displayedExamplesLabel}</th>
              </>
            )}
            {visibleSide !== 'employee' && (
              <>
                <th style={{ ...headerStyle, width: '22%', minWidth: 210 }}>{ratingInputLabel}</th>
                <th style={headerStyle}>{displayedExamplesLabel}</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {formStructure.map(category => {
            const question = category.questions.find(item => item.id === questionKey) || category.questions[0]
            if (!question) return null
            const employeeRating = getFieldValue(category.id, question.id, 'employee_rating')
            const employeeComments = getFieldValue(category.id, question.id, 'employee_comments')
            const managerRating = getFieldValue(category.id, question.id, 'manager_rating')
            const managerComments = getFieldValue(category.id, question.id, 'manager_comments')
            const employeeExampleMissing = examplesRequired && Boolean(employeeRating) && !employeeComments.trim()
            const managerExampleMissing = examplesRequired && Boolean(managerRating) && !managerComments.trim()

            return (
              <tr key={category.id}>
                <td style={{ ...cellStyle, whiteSpace: 'pre-line', fontWeight: 700 }}>{category.title}</td>
                {visibleSide !== 'manager' && (
                  <>
                    <td style={cellStyle}>
                      {['employee', 'both'].includes(editableSide)
                        ? renderEditableRating(category, question, employeeRating, 'employee')
                        : renderRating(employeeRating)}
                    </td>
                    <td style={cellStyle}>
                      {['employee', 'both'].includes(editableSide)
                        ? (
                          <>
                            <TextArea
                              rows={2}
                              value={employeeComments}
                              onChange={event => updateField(category.id, question.id, 'employee_comments', event.target.value)}
                              placeholder={displayedExamplesLabel}
                              status={showValidationErrors && employeeExampleMissing ? 'error' : undefined}
                            />
                            {showValidationErrors && employeeExampleMissing && (
                              <Text type='danger' style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                                {examplesLabel} are required.
                              </Text>
                            )}
                          </>
                        )
                        : <Text style={{ whiteSpace: 'pre-wrap' }}>{employeeComments || noExamplesLabel}</Text>}
                    </td>
                  </>
                )}
                {visibleSide !== 'employee' && (
                  <>
                    <td style={cellStyle}>
                      {['manager', 'both'].includes(editableSide)
                        ? renderEditableRating(category, question, managerRating, 'manager')
                        : (managerSubmitted ? renderRating(managerRating) : <Text type='secondary'>{pendingManagerReviewLabel}</Text>)}
                    </td>
                    <td style={cellStyle}>
                      {['manager', 'both'].includes(editableSide)
                        ? (
                          <>
                            <TextArea
                              rows={2}
                              value={managerComments}
                              onChange={event => updateField(category.id, question.id, 'manager_comments', event.target.value)}
                              placeholder={displayedExamplesLabel}
                              status={showValidationErrors && managerExampleMissing ? 'error' : undefined}
                            />
                            {showValidationErrors && managerExampleMissing && (
                              <Text type='danger' style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                                {examplesLabel} are required.
                              </Text>
                            )}
                          </>
                        )
                        : <Text style={{ whiteSpace: 'pre-wrap' }}>{managerSubmitted ? (managerComments || noExamplesLabel) : pendingManagerReviewLabel}</Text>}
                    </td>
                  </>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )

  return (
    <Card className='tti-appraisal-section-card' title={<Text style={{ whiteSpace: 'pre-line', color: '#FFF' }}>{title}</Text>} style={{ marginBottom: 24 }}>
      <Text type='secondary' style={{ display: 'block', marginBottom: 16, whiteSpace: 'pre-line' }}>
        {description}
      </Text>
      {showRatingScaleTable && (
        <div style={{ marginBottom: 24 }}>
          <RatingGuideTable source={formConfig} />
        </div>
      )}
      {renderTable()}
    </Card>
  )
}
